/**
 * Utility functions for formatting occasion dates and times
 */

/**
 * Format occasion information for display in template cards
 * @param {Object} template - Template object with occasion fields
 * @returns {string|null} - Formatted occasion string or null if no occasion
 */
export const formatOccasionDisplay = (template) => {
  if (!template || !template.isOccasionDateApplicable || template.occasionType === 'none') {
    return null
  }

  const { 
    occasionType, 
    occasionStartDate, 
    occasionEndDate, 
    occasionStartTime, 
    occasionEndTime,
    isRecurringYearly,
    weeklyRecurrenceDays,
    monthlyRecurrenceDays 
  } = template

  try {
    switch (occasionType) {
      case 'specific_date':
        return formatSpecificDate(occasionStartDate, occasionEndDate, occasionStartTime, occasionEndTime)
      
      case 'date_range':
        return formatDateRange(occasionStartDate, occasionEndDate, occasionStartTime, occasionEndTime)
      
      case 'recurring_yearly':
        return formatRecurringYearly(occasionStartDate, occasionEndDate, occasionStartTime, occasionEndTime)
      
      case 'weekly':
        return formatWeekly(weeklyRecurrenceDays, occasionStartTime, occasionEndTime)
      
      case 'monthly':
        return formatMonthly(monthlyRecurrenceDays, occasionStartTime, occasionEndTime)
      
      default:
        return null
    }
  } catch (error) {
    console.warn('Error formatting occasion display:', error)
    return 'Occasion scheduled'
  }
}

/**
 * Format specific date occasion
 */
const formatSpecificDate = (startDate, endDate, startTime, endTime) => {
  if (!startDate) return null
  
  const start = new Date(startDate)
  const end = endDate ? new Date(endDate) : null
  
  // Format date
  let dateStr = start.toLocaleDateString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    month: 'short', 
    day: 'numeric',
    year: start.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  })
  
  // Add end date if different
  if (end && end.getTime() !== start.getTime()) {
    const endStr = end.toLocaleDateString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      month: 'short', 
      day: 'numeric',
      year: end.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    })
    dateStr += ` - ${endStr}`
  }
  
  // Add time if specified
  const timeStr = formatTimeRange(startTime, endTime)
  
  return timeStr ? `📅 ${dateStr} ${timeStr}` : `📅 ${dateStr}`
}

/**
 * Format date range occasion
 */
const formatDateRange = (startDate, endDate, startTime, endTime) => {
  if (!startDate || !endDate) return null
  
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  const startStr = start.toLocaleDateString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    month: 'short', 
    day: 'numeric'
  })
  const endStr = end.toLocaleDateString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    month: 'short', 
    day: 'numeric',
    year: end.getFullYear() !== start.getFullYear() ? 'numeric' : undefined
  })
  
  const timeStr = formatTimeRange(startTime, endTime)
  const dateRange = `${startStr} - ${endStr}`
  
  return timeStr ? `📅 ${dateRange} ${timeStr}` : `📅 ${dateRange}`
}

/**
 * Format recurring yearly occasion
 */
const formatRecurringYearly = (startDate, endDate, startTime, endTime) => {
  if (!startDate) return null
  
  const start = new Date(startDate)
  const end = endDate ? new Date(endDate) : null
  
  let dateStr = start.toLocaleDateString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    month: 'short', 
    day: 'numeric'
  })
  
  if (end && end.getTime() !== start.getTime()) {
    const endStr = end.toLocaleDateString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      month: 'short', 
      day: 'numeric'
    })
    dateStr += ` - ${endStr}`
  }
  
  const timeStr = formatTimeRange(startTime, endTime)
  
  return timeStr ? `🔄 ${dateStr} (yearly) ${timeStr}` : `🔄 ${dateStr} (yearly)`
}

/**
 * Format weekly recurrence
 */
const formatWeekly = (weeklyDays, startTime, endTime) => {
  if (!weeklyDays || !Array.isArray(weeklyDays) || weeklyDays.length === 0) {
    return null
  }
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const days = weeklyDays.sort().map(day => dayNames[day]).join(', ')
  
  const timeStr = formatTimeRange(startTime, endTime)
  
  return timeStr ? `📆 ${days} ${timeStr}` : `📆 ${days}`
}

/**
 * Format monthly recurrence
 */
const formatMonthly = (monthlyDays, startTime, endTime) => {
  if (!monthlyDays || !Array.isArray(monthlyDays) || monthlyDays.length === 0) {
    return null
  }
  
  let dayStr = ''
  if (monthlyDays.includes('last')) {
    dayStr = 'Last day'
  } else if (monthlyDays.some(day => typeof day === 'string')) {
    // Handle special days like 'first_monday', 'third_friday'
    dayStr = monthlyDays.filter(day => typeof day === 'string').join(', ')
  } else {
    // Handle numeric days
    const numericDays = monthlyDays.filter(day => typeof day === 'number').sort()
    dayStr = numericDays.map(day => `${day}${getOrdinalSuffix(day)}`).join(', ')
  }
  
  const timeStr = formatTimeRange(startTime, endTime)
  
  return timeStr ? `📅 ${dayStr} (monthly) ${timeStr}` : `📅 ${dayStr} (monthly)`
}

/**
 * Format time range
 */
const formatTimeRange = (startTime, endTime) => {
  if (!startTime && !endTime) return ''
  
  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    
    // Handle HH:MM format
    const [hours, minutes] = timeStr.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    
    return date.toLocaleTimeString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }
  
  const start = formatTime(startTime)
  const end = formatTime(endTime)
  
  if (start && end) {
    // Don't show time range if it's the full day (00:01 - 23:59)
    if (startTime === '00:01' && endTime === '23:59') {
      return '(all day)'
    }
    return `(${start} - ${end})`
  } else if (start) {
    return `(from ${start})`
  } else if (end) {
    return `(until ${end})`
  }
  
  return ''
}

/**
 * Get ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
 */
const getOrdinalSuffix = (num) => {
  const j = num % 10
  const k = num % 100
  
  if (j === 1 && k !== 11) return 'st'
  if (j === 2 && k !== 12) return 'nd'
  if (j === 3 && k !== 13) return 'rd'
  return 'th'
}

/**
 * Get a short occasion status for badges
 * @param {Object} template - Template object
 * @returns {string|null} - Short status text or null
 */
export const getOccasionStatus = (template) => {
  if (!template || !template.isOccasionDateApplicable || template.occasionType === 'none') {
    return null
  }
  
  switch (template.occasionType) {
    case 'specific_date':
      return 'Scheduled'
    case 'date_range':
      return 'Date Range'
    case 'recurring_yearly':
      return 'Yearly'
    case 'weekly':
      return 'Weekly'
    case 'monthly':
      return 'Monthly'
    default:
      return 'Occasion'
  }
}

/**
 * Check if an occasion is currently active
 * @param {Object} template - Template object
 * @returns {boolean} - Whether the occasion is currently active
 */
export const isOccasionActive = (template) => {
  if (!template || !template.isOccasionDateApplicable || template.occasionType === 'none') {
    return false
  }
  
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  
  try {
    switch (template.occasionType) {
      case 'specific_date':
        if (template.occasionStartDate) {
          const startDate = new Date(template.occasionStartDate)
          const endDate = template.occasionEndDate ? new Date(template.occasionEndDate) : startDate
          return today >= startDate && today <= endDate
        }
        return false
      
      case 'date_range':
        if (template.occasionStartDate && template.occasionEndDate) {
          const startDate = new Date(template.occasionStartDate)
          const endDate = new Date(template.occasionEndDate)
          return today >= startDate && today <= endDate
        }
        return false
      
      case 'recurring_yearly':
        if (template.occasionStartDate) {
          const startDate = new Date(template.occasionStartDate)
          const endDate = template.occasionEndDate ? new Date(template.occasionEndDate) : startDate
          
          // Check if today matches the yearly recurrence
          const currentYearStart = new Date(now.getFullYear(), startDate.getMonth(), startDate.getDate())
          const currentYearEnd = new Date(now.getFullYear(), endDate.getMonth(), endDate.getDate())
          
          return today >= currentYearStart && today <= currentYearEnd
        }
        return false
      
      case 'weekly':
        if (template.weeklyRecurrenceDays && Array.isArray(template.weeklyRecurrenceDays)) {
          const dayOfWeek = now.getDay() // 0 = Sunday, 1 = Monday, etc.
          return template.weeklyRecurrenceDays.includes(dayOfWeek)
        }
        return false
      
      case 'monthly':
        if (template.monthlyRecurrenceDays && Array.isArray(template.monthlyRecurrenceDays)) {
          const dayOfMonth = now.getDate()
          return template.monthlyRecurrenceDays.includes(dayOfMonth) || 
                 (template.monthlyRecurrenceDays.includes('last') && isLastDayOfMonth(now))
        }
        return false
      
      default:
        return false
    }
  } catch (error) {
    console.warn('Error checking occasion active status:', error)
    return false
  }
}

/**
 * Check if a date is the last day of the month
 */
const isLastDayOfMonth = (date) => {
  const nextDay = new Date(date)
  nextDay.setDate(date.getDate() + 1)
  return nextDay.getMonth() !== date.getMonth()
}
